import { useQuery } from "@tanstack/react-query";
import { Loader2, Phone } from "lucide-react";
import { get } from "@/lib/api/_client";
import { useT } from "@/lib/i18n/use-t";
import { T } from "./call-widget.i18n";

interface CallData {
  id: string;
  clientId: string;
  topic: string;
  content: string | null;
  summary: string | null;
}

interface CallWidgetProps {
  call: {
    id: string;
    topic: string;
    preview: string;
    occurredAt: string;
    authorName: string | null;
  };
  onActivityClick?: (url: string) => void;
}

export function CallWidget({ call: initial, onActivityClick }: CallWidgetProps) {
  const t = useT(T);
  const hasInitial = Boolean(initial.topic && initial.preview);
  const { data, isLoading } = useQuery({
    queryKey: ["client-calls", "single", initial.id],
    queryFn: () => get<CallData>(`/internal/client-calls/single/${initial.id}`),
    enabled: !hasInitial,
    staleTime: 60_000,
  });

  if (isLoading) {
    return (
      <div className="flex items-center gap-2 text-xs p-2 rounded bg-muted/50">
        <Loader2 className="h-3 w-3 animate-spin" />
        <span className="text-muted-foreground">{t.loading}</span>
      </div>
    );
  }

  const topic = data?.topic || initial.topic || t.fallbackTitle;
  const preview = (data?.summary || data?.content || initial.preview || "").trim().slice(0, 100);
  const href = data?.clientId ? `/clients/${data.clientId}?tab=calls&id=${initial.id}` : null;

  const card = (
    <>
      <div className="h-6 w-6 rounded bg-primary/10 flex items-center justify-center flex-shrink-0">
        <Phone className="h-3 w-3 text-primary" />
      </div>
      <div className="flex-1 min-w-0">
        <div className="font-medium truncate">{topic}</div>
        {preview && <div className="text-muted-foreground truncate">{preview}</div>}
      </div>
    </>
  );

  return href ? (
    <button
      type="button"
      onClick={() => onActivityClick?.(href)}
      className="w-full text-left flex items-start gap-2 text-xs p-2 rounded bg-muted/50 hover:bg-accent hover:text-accent-foreground transition-colors cursor-pointer border border-transparent hover:border-primary/20"
    >
      {card}
    </button>
  ) : (
    <div className="w-full flex items-start gap-2 text-xs p-2 rounded bg-muted/50">{card}</div>
  );
}
